'use client';

import { useState } from 'react';
import Image from 'next/image';

import { IconUser, IconBook2, IconNews } from '@tabler/icons-react';

import { PlaceholderWrap } from './media-image.elements';

const variantIcons = {
  lecturer: IconUser,
  education: IconBook2,
  post: IconNews,
};

/**
 * MediaImage — drop-in replacement for next/image used across CMS-driven
 * featured images (educations, lecturers, posts). When `src` is missing or
 * the underlying image fails to load, a stylish themed placeholder is shown
 * instead of a broken-image icon.
 *
 * Props mirror next/image (`src`, `alt`, `width`, `height`, `style`, ...).
 * Extra:
 *   - `variant`: 'lecturer' | 'education' | 'post' (controls placeholder)
 */
const MediaImage = ({ src, alt, width, height, style, variant = 'post', ...rest }) => {
  const [errored, setErrored] = useState(false);

  const showPlaceholder = !src || errored;

  if (showPlaceholder) {
    const Icon = variantIcons[variant] || variantIcons.post;
    // Size the placeholder to match what the image would have occupied.
    // Use the smaller dimension to size the icon so it looks balanced
    // in both square (lecturer) and landscape (card) frames.
    const numericWidth = Number(width) || 300;
    const numericHeight = Number(height) || 215;
    const iconSize = Math.round(Math.min(numericWidth, numericHeight) * 0.45);

    return (
      <PlaceholderWrap
        $variant={variant}
        style={{
          width: `${numericWidth}px`,
          height: `${numericHeight}px`,
          maxWidth: '100%',
          // Allow callers to override (e.g. `width: 100%`, `height: 100%`)
          ...style,
        }}
        role="img"
        aria-label={alt || 'Slika nije dostupna'}
      >
        <Icon size={iconSize} stroke={1.25} />
      </PlaceholderWrap>
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width}
      height={height}
      style={style}
      onError={() => setErrored(true)}
      {...rest}
    />
  );
};

export { MediaImage };
